import { get, set } from 'idb-keyval';
import { createStore } from 'zustand/vanilla';
import { useStore } from 'zustand';
import { getAssetBlob, getAssetMeta, markUploaded, assetsIdb } from '../board/assets/localAssets';
import { api, ApiError, NetworkError } from './api';

/**
 * Persistent queue of assets waiting to reach the server. Survives reloads (IndexedDB),
 * retries on network failure, and drops jobs the server permanently rejects.
 */

interface Job {
  boardId: string;
  assetId: string;
}

const KEY = 'uploadQueue';
let queue: Job[] = [];
let loaded = false;
let running = false;
let attempts = 0;

export const uploadStore = createStore<{ pending: number; failed: number }>(() => ({ pending: 0, failed: 0 }));
export const useUploads = <T>(sel: (s: { pending: number; failed: number }) => T) => useStore(uploadStore, sel);

async function persist(): Promise<void> {
  uploadStore.setState({ pending: queue.length });
  await set(KEY, queue, assetsIdb);
}

export async function startUploadQueue(): Promise<void> {
  if (!loaded) {
    loaded = true;
    queue = (await get<Job[]>(KEY, assetsIdb)) ?? [];
    uploadStore.setState({ pending: queue.length });
    window.addEventListener('online', () => void kick());
  }
  void kick();
}

export async function enqueueUpload(boardId: string, assetId: string): Promise<void> {
  if (!loaded) await startUploadQueue();
  if (!queue.some((j) => j.assetId === assetId)) queue.push({ boardId, assetId });
  await persist();
  void kick();
}

export function kick(): Promise<void> {
  if (running) return Promise.resolve();
  running = true;
  return (async () => {
    while (queue.length) {
      const job = queue[0]!;
      try {
        await uploadOne(job);
        queue.shift();
        attempts = 0;
        await persist();
      } catch (e) {
        if (e instanceof ApiError && e.status < 500 && e.status !== 401 && e.status !== 429) {
          console.warn(`[upload] dropping asset ${job.assetId}: ${e.message}`);
          queue.shift();
          uploadStore.setState({ failed: uploadStore.getState().failed + 1 });
          await persist();
          continue;
        }
        attempts++;
        const delay = Math.min(60000, 3000 * 2 ** Math.min(attempts, 5));
        if (!(e instanceof NetworkError)) console.warn('[upload] retrying later:', e);
        await new Promise((r) => setTimeout(r, delay));
      }
    }
  })().finally(() => {
    running = false;
  });
}

async function uploadOne(job: Job): Promise<void> {
  const meta = await getAssetMeta(job.assetId);
  if (!meta) return; // deleted locally; nothing to send
  if (meta.uploaded) return;
  const form = new FormData();
  form.append('meta', JSON.stringify({ id: meta.id, name: meta.name, width: meta.width, height: meta.height, kind: meta.kind ?? 'image' }));
  const original = await getAssetBlob(job.assetId, 'original', false);
  if (!original) throw new ApiError(400, 'original blob missing locally');
  form.append('original', original, meta.name || 'image');
  for (const v of ['thumb', 'medium', 'large'] as const) {
    if (!meta.variants[v]) continue;
    const blob = await getAssetBlob(job.assetId, v, false);
    if (blob) form.append(v, blob, `${v}.webp`);
  }
  await api.upload(`/boards/${job.boardId}/assets`, form);
  await markUploaded(job.assetId);
}
